import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  connectAgencyClient,
  createAgency,
  deleteAgency,
  disconnectAgencyClient,
  getAgencies,
  getAgencyById,
  getAgencyClients,
  getAgencyUsers,
  GetAgenciesParams,
  getLinkableClients,
  inviteAgencyUsers,
  removeAgencyUser,
  updateAgency,
  updateAgencyUser,
} from "@/services/agency";
import { retryUnlessClientError } from "@/lib/queryRetry";
import type {
  ConnectAgencyClientDto,
  CreateAgencyDto,
  GetAgencyClientsParams,
  GetAgencyUsersParams,
  GetLinkableClientsParams,
  InviteAgencyUserDto,
  UpdateAgencyDto,
  UpdateAgencyUserDto,
} from "@/types";

export const AGENCIES_QUERY_KEY = "agencies";

/**
 * How often to re-read the clients list while a disconnect is running.
 *
 * The cascade deletes trips, deliverables, photos and comments, so it is not
 * instant; three seconds is often enough to feel live without hammering an
 * endpoint that is already doing heavy work.
 */
export const CLIENT_REMOVAL_POLL_MS = 3000;

/**
 * Hook to fetch agencies with pagination, search and sorting
 */
export function useAgencies(params?: GetAgenciesParams) {
  return useQuery({
    queryKey: [AGENCIES_QUERY_KEY, params],
    queryFn: () => getAgencies(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch a single agency.
 *
 * The detail screen reads through this rather than a local effect so a mutation
 * anywhere can refresh it by invalidating the agencies key.
 */
export function useAgency(agencyId: string) {
  return useQuery({
    queryKey: [AGENCIES_QUERY_KEY, "detail", agencyId],
    queryFn: () => getAgencyById(agencyId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!agencyId,
  });
}

/**
 * Hook to fetch the people in an agency with pagination and search
 */
export function useAgencyUsers(agencyId: string, params?: GetAgencyUsersParams) {
  return useQuery({
    queryKey: [AGENCIES_QUERY_KEY, "users", agencyId, params],
    queryFn: () => getAgencyUsers(agencyId, params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!agencyId,
  });
}

/**
 * Hook to create an agency
 */
export function useCreateAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgencyDto) => createAgency(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to rename an agency
 */
export function useUpdateAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agencyId,
      data,
    }: {
      agencyId: string;
      data: UpdateAgencyDto;
    }) => updateAgency(agencyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete an agency
 */
export function useDeleteAgency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agencyId: string) => deleteAgency(agencyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to invite one or several people into an agency.
 *
 * Resolves 2xx even when some rows failed — the caller reads `failureCount` and
 * `results` to report which addresses to fix.
 */
export function useInviteAgencyUsers(agencyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteAgencyUserDto) =>
      inviteAgencyUsers(agencyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to change an agency person's role
 */
export function useUpdateAgencyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UpdateAgencyUserDto;
    }) => updateAgencyUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to remove a person from an agency
 */
export function useRemoveAgencyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeAgencyUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to fetch the venues an agency works for, with pagination.
 *
 * `pollWhileRemoving` turns on a repeat fetch, and exists because
 * DISCONNECTING IS ASYNCHRONOUS. `DELETE .../clients/:venueId` answers 202, so
 * the only way to learn the job finished is that the row stops coming back —
 * the backend deletes the connection row last, which makes its disappearance a
 * truthful completion signal. The caller switches this on while any row is
 * being removed and off again once none is.
 */
export function useAgencyClients(
  agencyId: string,
  params?: GetAgencyClientsParams,
  pollWhileRemoving = false,
) {
  return useQuery({
    queryKey: [AGENCIES_QUERY_KEY, "clients", agencyId, params],
    queryFn: ({ signal }) => getAgencyClients(agencyId, params, signal),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!agencyId,
    refetchInterval: pollWhileRemoving ? CLIENT_REMOVAL_POLL_MS : false,
    // A retried 401 re-fires the interceptor's redirect to /login — once per
    // poll tick while a removal is running.
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to search the venues this agency could be connected to.
 *
 * Runs with or without a search term, so the picker is populated as soon as it
 * opens; the caller's `pageSize` bounds the unfiltered read. `enabled` lets the
 * caller hold the read until the picker is actually open.
 *
 * Never served from cache: which venues are linkable changes whenever any
 * agency connects or disconnects one, and a 5-minute-old list offers venues
 * that are already taken. Pair with `useClearLinkableClients` on open, which
 * drops the list the disabled observer is still holding.
 */
export function useLinkableClients(
  agencyId: string,
  params?: GetLinkableClientsParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...linkableClientsKey(agencyId), params],
    // Signalled: this refires on every debounced keystroke, so an abandoned
    // search should not stay in flight behind the one the admin is waiting on.
    queryFn: ({ signal }) => getLinkableClients(agencyId, params, signal),
    staleTime: 0,
    gcTime: 0,
    enabled: !!agencyId && (options?.enabled ?? true),
    retry: retryUnlessClientError,
  });
}

const linkableClientsKey = (agencyId: string) =>
  [AGENCIES_QUERY_KEY, "linkable-clients", agencyId] as const;

/**
 * Drops every cached linkable-clients result for this agency. Call it just
 * before the picker opens: `gcTime: 0` alone is not enough, because the hook
 * stays mounted (disabled) while the picker is closed and so keeps its last
 * result — reopening would paint that stale list first. Removed, the reopened
 * query starts empty and shows its loading state until the fresh list lands.
 */
export function useClearLinkableClients(agencyId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.removeQueries({ queryKey: linkableClientsKey(agencyId) }),
    [queryClient, agencyId],
  );
}

/**
 * Hook to connect a venue to an agency.
 *
 * Nothing is pre-checked here. The server owns the refusals — 404 unknown
 * venue, 400 venue without marketing, 409 already connected — and the caller
 * renders whichever comes back.
 */
export function useConnectAgencyClient(agencyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConnectAgencyClientDto) =>
      connectAgencyClient(agencyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to disconnect a venue from an agency.
 *
 * Resolving means ACCEPTED, NOT FINISHED — the endpoint answers 202 and the
 * cascade runs in the background. Do not report a completed removal from
 * `onSuccess`; the caller keeps the row, disables its delete control and waits
 * for the row to stop coming back from `useAgencyClients`.
 */
export function useDisconnectAgencyClient(agencyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (venueId: string) => disconnectAgencyClient(agencyId, venueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENCIES_QUERY_KEY] });
    },
  });
}
